/**
 * Vœux d'une candidature.
 *
 * Une fiche « Candidature en test » (Candidature__c) porte jusqu'à trois
 * filières. Chaque filière occupe un emplacement numéroté 1, 2 ou 3, avec ses
 * propres champs (Niveau_2__c, Filiere_2__c, Avis_du_responsable_2__c…).
 *
 * Côté portail, chaque filière du dossier (une entrée de `data`) est un vœu.
 * Son numéro est fixé à la création, d'après la position de filiere1/2/3 dans
 * l'appel Salesforce, et revérifié contre la fiche à chaque envoi.
 */

const VOEUX = [1, 2, 3];
const SOBJECT = 'Candidature__c';
const STAGE = 'Etape_de_la_candidature__c';
const WAITING = 'Attente avis responsable';

// État propre à chaque vœu : un candidat envoie ses vœux au fil de la
// candidature, l'étape globale ne dit pas lesquels sont encore envisagés.
const STATUT = {
  NON_ENVOYE: 'Non envoyé',
  ENVOYE: 'Envoyé',
  AVIS_DEMANDE: 'Avis demandé',
  AVIS_RENDU: 'Avis rendu',
  ABANDONNE: 'Abandonné'
};

// Champs répétés pour chaque vœu. Le lien du vœu 1 garde le nom du champ
// historique, créé avant la prise en charge de plusieurs vœux.
const WISH_FIELD_FAMILIES = [
  { key: 'niveau', label: 'Niveau', field: (n) => `Niveau_${n}__c` },
  { key: 'filiere', label: 'Filière', field: (n) => `Filiere_${n}__c` },
  { key: 'filiereObjet', label: 'Filière objet', field: (n) => `Fili_re_${n}_objet__c` },
  { key: 'parcours', label: 'Parcours', field: (n) => `Parcours_${n}__c` },
  { key: 'admission', label: 'Admission', field: (n) => `Admission_${n}__c` },
  { key: 'avis', label: 'Avis du responsable', field: (n) => `Avis_du_responsable_${n}__c` },
  { key: 'commentaire', label: 'Commentaire', field: (n) => `Commentaire_${n}__c` },
  { key: 'lien', label: 'Lien avis responsable', field: (n) => (n === 1 ? 'Lien_avis_responsable__c' : `Lien_avis_responsable_${n}__c`) },
  { key: 'statut', label: 'Statut vœu', field: (n) => `Statut_voeu_${n}__c` }
];

const isVoeu = (value) => VOEUX.includes(value);

function wishField(key, voeu) {
  const family = WISH_FIELD_FAMILIES.find((item) => item.key === key);
  if (!family || !isVoeu(voeu)) throw new Error(`Champ de vœu inconnu : ${key} ${voeu}.`);
  return family.field(voeu);
}

/** Famille et numéro de vœu d'un champ Candidature__c, ou null. */
function wishFieldInfo(sobject, field) {
  if (sobject !== SOBJECT) return null;
  for (const family of WISH_FIELD_FAMILIES) {
    const voeu = VOEUX.find((n) => family.field(n) === field);
    if (voeu) return { family, voeu };
  }
  return null;
}

/** Même champ, pour le vœu demandé. Les autres champs sont inchangés. */
function retargetWishField(sobject, field, voeu) {
  const info = wishFieldInfo(sobject, field);
  return info && isVoeu(voeu) ? info.family.field(voeu) : field;
}

/**
 * Clé d'unicité d'une correspondance : deux lignes vers Niveau_1__c et
 * Niveau_2__c écriraient le même champ une fois ramenées au vœu envoyé.
 */
function mappingTargetKey(sobject, field) {
  const info = wishFieldInfo(sobject, field);
  return info ? `${sobject}.${info.family.field('N')}` : `${sobject}.${field}`;
}

/**
 * Numéro de vœu d'une filière du dossier, confronté à la fiche Salesforce.
 * Le numéro enregistré prime s'il désigne toujours cette filière ; sinon on
 * retient l'unique emplacement qui la porte. Null si aucun ou plusieurs.
 */
function resolveVoeu(entry, record) {
  if (!entry || !record) return null;
  const nom = String(entry.nom || '').trim();
  const niveau = String(entry.niveau || '').trim();
  if (!nom) return null;
  const matches = VOEUX.filter((n) => {
    const filiere = String(record[wishField('filiere', n)] || '').trim();
    const level = String(record[wishField('niveau', n)] || '').trim();
    return filiere === nom && (!niveau || !level || level === niveau);
  });
  if (isVoeu(entry.voeu) && matches.includes(entry.voeu)) return entry.voeu;
  return matches.length === 1 ? matches[0] : null;
}

/** Filière transmise par le candidat pour ce vœu de la fiche, ou null. */
function submittedEntryForVoeu(entries, record, voeu) {
  const found = (entries || []).filter((entry) => entry && entry.soumis === true
    && resolveVoeu(entry, record) === voeu);
  return found.length === 1 ? found[0] : null;
}

/** Vœux transmis par le candidat, tels que la fiche Salesforce les situe. */
function submittedVoeux(entries, record) {
  return VOEUX.filter((voeu) => submittedEntryForVoeu(entries, record, voeu));
}

/**
 * L'avis de ce vœu est-il demandé ? Avec les champs « Statut vœu N », c'est le
 * service qui le décide vœu par vœu (« Avis demandé »), quelle que soit l'étape
 * globale. Sans eux (champ absent de la fiche lue), on retombe sur l'étape.
 */
function isReviewRequested(record, voeu) {
  const field = wishField('statut', voeu);
  return field in record ? record[field] === STATUT.AVIS_DEMANDE : record[STAGE] === WAITING;
}

/** Vœux transmis dont l'avis est demandé et pas encore rendu. */
function voeuxAwaitingReview(entries, record) {
  return submittedVoeux(entries, record)
    .filter((voeu) => isReviewRequested(record, voeu) && !record[wishField('avis', voeu)]);
}

module.exports = {
  VOEUX,
  STAGE,
  WAITING,
  STATUT,
  isReviewRequested,
  WISH_FIELD_FAMILIES,
  isVoeu,
  wishField,
  wishFieldInfo,
  retargetWishField,
  mappingTargetKey,
  resolveVoeu,
  submittedEntryForVoeu,
  submittedVoeux,
  voeuxAwaitingReview
};
